import { useMemo } from 'react';
import dayjs from 'dayjs';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useResourceSets,
  type ResourceSet,
} from 'components/IAP/hooks/resourceSetHooks';
import { useOrgActions } from 'components/IAP/hooks/orgActionHooks';
import { useGroupGuid } from 'components/IAP/hooks/commonHooks';
import { ORGANIZATION } from 'components/IAP/ICSConstants';
import { useAors } from 'hooks/useAors';
import { useAorText } from 'utils/aorTerminology';
import { useGetRostersForGroup } from 'hooks/incident/useRoster';
import { NULL_VALUE } from 'constants/displayConstants';
import IncidentDetailsSummary from 'components/MapPage/CorporateViewTabs/ResourceTab/IncidentDetailsSummary';
import { USCG_SYMBOLOGY_OPTIONS } from 'components/MapPage/CorporateViewTabs/LayersTab/utils/uscgSymbology';
import type { ReportMentionRef } from './reportMentionTypes';

type ReportMentionDetailDialogProps = {
  mention: ReportMentionRef | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

function DetailRow({
  label,
  value,
  hideWhenEmpty = false,
}: {
  label: string;
  value?: string | null;
  /** When true, omit the row if value is empty (used for action mentions). */
  hideWhenEmpty?: boolean;
}) {
  const empty = value == null || value === '';
  if (hideWhenEmpty && empty) return null;
  return (
    <div className="tw:grid tw:grid-cols-[9rem_1fr] tw:gap-2 tw:text-sm">
      <span className="tw:text-muted-foreground">{label}</span>
      <span className="tw:min-w-0 tw:break-words">
        {empty ? NULL_VALUE : value}
      </span>
    </div>
  );
}

function formatDateTime(value?: string | null): string | null {
  if (!value) return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('MM/DD/YYYY HH:mm') : value;
}

function formatCoord(n?: number | null): string | null {
  if (n == null || Number.isNaN(n)) return null;
  return String(Math.round(n * 100) / 100);
}

function resolveAorName(
  aors: { id: string; name: string }[],
  aorId: unknown
): string | null {
  if (aorId == null || aorId === '') return null;
  const id = String(aorId);
  return aors.find((a) => a.id === id)?.name || id;
}

function formatPoc(
  pointOfContact: unknown,
  groupMembers: {
    user_guid?: string;
    email_address?: string;
    user_name?: string;
  }[]
): string | null {
  if (pointOfContact == null || pointOfContact === '') return null;
  if (typeof pointOfContact === 'string') {
    const member = groupMembers.find((m) => m.user_guid === pointOfContact);
    return member?.email_address || member?.user_name || pointOfContact || null;
  }
  if (typeof pointOfContact === 'object') {
    const poc = pointOfContact as {
      emailAddress?: string;
      email_address?: string;
      userName?: string;
      user_name?: string;
      email?: string;
    };
    return (
      poc.emailAddress ||
      poc.email_address ||
      poc.email ||
      poc.userName ||
      poc.user_name ||
      null
    );
  }
  return null;
}

function formatAssignees(resource: ResourceSet): string | null {
  const details = resource.resource_in_incident_details;
  if (!details?.length) return null;
  const names = details
    .flatMap((detail) => detail.assignees || [])
    .map((a) => a.name || a.email)
    .filter(Boolean);
  return names.length ? names.join(', ') : null;
}

function SymbologyValue({ symbology }: { symbology?: string | null }) {
  if (!symbology) {
    return <span className="tw:min-w-0 tw:break-words">{NULL_VALUE}</span>;
  }
  const match = USCG_SYMBOLOGY_OPTIONS.find((opt) => opt.type === symbology);
  if (!match) {
    return <span className="tw:min-w-0 tw:break-words">{symbology}</span>;
  }
  return (
    <span className="tw:inline-flex tw:min-w-0 tw:items-center tw:gap-1 tw:break-words">
      <img src={match.iconSrc} alt="" className="tw:h-4 tw:w-4 tw:shrink-0" />
      <span>{match.label}</span>
    </span>
  );
}

function ResourceDetailRows({ resource }: { resource: ResourceSet }) {
  const aorText = useAorText();
  const { data: aors = [] } = useAors();
  const groupId = useGroupGuid();
  const { data: groupMembers = [] } = useGetRostersForGroup(groupId ?? '');

  const lat = resource.geometry?.coordinates?.[1];
  const lng = resource.geometry?.coordinates?.[0];
  const assetStatusTimestamp = formatDateTime(resource.asset_status_timestamp);
  const assetStatusDisplay = resource.asset_status
    ? assetStatusTimestamp
      ? `${resource.asset_status} (${assetStatusTimestamp})`
      : resource.asset_status
    : null;

  const incidentDetail = resource.incident_details?.[0];

  return (
    <>
      <DetailRow
        label="Resource ID"
        value={resource.resource_set_id?.slice(0, 5) || null}
      />
      <DetailRow
        label="Name"
        value={
          resource.owning_organization
            ? `${resource.name} (EXTERNAL)`
            : resource.name
        }
      />
      <DetailRow label="Kind" value={resource.kind} />
      <DetailRow label="Type" value={resource.type || 'General'} />
      <DetailRow
        label={aorText('AOR')}
        value={resolveAorName(aors, resource.aor)}
      />
      <DetailRow label="Incident" value={incidentDetail?.name || null} />
      <DetailRow
        label="Work Availability"
        value={resource.current_status || resource.status}
      />
      <DetailRow
        label="Request Status"
        value={resource.in_request ? 'Requested' : 'Not Requested'}
      />
      <DetailRow label="Asset Status" value={assetStatusDisplay} />
      <DetailRow label="Assignees" value={formatAssignees(resource)} />
      <DetailRow label="Current Location" value={resource.address} />
      <DetailRow label="OPCON" value={resolveAorName(aors, resource.opcon)} />
      <DetailRow label="TACON" value={resolveAorName(aors, resource.tacon)} />
      <DetailRow
        label="Point of Contact"
        value={formatPoc(resource.point_of_contact, groupMembers)}
      />
      <DetailRow
        label="Owning Organization"
        value={resource.owning_organization}
      />
      <DetailRow
        label="Quantity"
        value={resource.quantity != null ? String(resource.quantity) : null}
      />
      <DetailRow label="Unit" value={resource.unit} />
      <DetailRow label="Hull/Tail Number" value={resource.hull_tail_number} />
      <div className="tw:grid tw:grid-cols-[9rem_1fr] tw:gap-2 tw:text-sm">
        <span className="tw:text-muted-foreground">Symbology</span>
        <SymbologyValue symbology={resource.symbology} />
      </div>
      <DetailRow label="Lat" value={formatCoord(lat)} />
      <DetailRow label="Long" value={formatCoord(lng)} />
      <DetailRow label="Capabilities" value={resource.description} />
      <DetailRow label="Notes" value={resource.notes} />
      <DetailRow
        label="Date Ordered"
        value={formatDateTime(resource.date_time_ordered)}
      />
      <DetailRow label="ETA" value={formatDateTime(resource.eta)} />

      {incidentDetail?.id ? (
        <div className="tw:mt-3 tw:rounded-md tw:border tw:border-border tw:p-3">
          <IncidentDetailsSummary
            incidentId={incidentDetail.id}
            resourceSetId={resource.resource_set_id}
            datetimeOrdered={resource.date_time_ordered}
            datetimeOrderedOffset={resource.datetime_ordered_offset}
            eta={resource.eta}
            etaTimezoneOffset={resource.eta_timezone_offset}
            notes={resource.notes}
          />
        </div>
      ) : null}
    </>
  );
}

export function ReportMentionDetailDialog({
  mention,
  open,
  onOpenChange,
}: ReportMentionDetailDialogProps) {
  const { data: resourceSets = [], isLoading: resourcesLoading } =
    useResourceSets();
  const { data: orgActions = [], isLoading: actionsLoading } =
    useOrgActions(ORGANIZATION);

  const resource = useMemo(() => {
    if (!mention || mention.kind !== 'resource') return null;
    return (
      resourceSets.find(
        (set) =>
          String(set.resource_set_id) === mention.id ||
          String(set.id) === mention.id
      ) ?? null
    );
  }, [mention, resourceSets]);

  const action = useMemo(() => {
    if (!mention || mention.kind !== 'action') return null;
    return (
      orgActions.find(
        (row) =>
          String(row.action_id) === mention.id || String(row.id) === mention.id
      ) ?? null
    );
  }, [mention, orgActions]);

  const isLoading = resourcesLoading || actionsLoading;
  const title =
    mention?.kind === 'resource'
      ? resource?.name || mention.label
      : mention?.kind === 'action'
        ? action?.name || mention.label
        : 'Mention';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="tw:sm:max-w-lg">
        <DialogHeader className="tw:pr-10">
          <DialogTitle className="tw:flex tw:min-w-0 tw:flex-col tw:items-start tw:gap-2">
            {mention ? (
              <Badge variant="outline" className="tw:capitalize tw:shrink-0">
                {mention.kind}
              </Badge>
            ) : null}
            <span className="tw:min-w-0 tw:break-words">{title}</span>
          </DialogTitle>
          <DialogDescription>
            {mention?.kind === 'resource'
              ? 'Resource details from your organization'
              : mention?.kind === 'action'
                ? 'Action details from your organization'
                : 'Mention details'}
          </DialogDescription>
        </DialogHeader>

        <div className="tw:max-h-[min(70vh,32rem)] tw:space-y-2 tw:overflow-y-auto tw:py-1 tw:pr-1">
          {isLoading ? (
            <p className="tw:text-sm tw:text-muted-foreground">Loading…</p>
          ) : mention?.kind === 'resource' && resource ? (
            <ResourceDetailRows resource={resource} />
          ) : mention?.kind === 'action' && action ? (
            <>
              <DetailRow label="Name" value={action.name} hideWhenEmpty />
              <DetailRow label="Status" value={action.status} hideWhenEmpty />
              <DetailRow
                label="POC"
                value={action.poc_user_name || action.poc}
                hideWhenEmpty
              />
              <DetailRow
                label="Start"
                value={
                  action.start_date
                    ? new Date(action.start_date).toLocaleString()
                    : null
                }
                hideWhenEmpty
              />
              <DetailRow
                label="Target"
                value={
                  action.target_date
                    ? new Date(action.target_date).toLocaleString()
                    : null
                }
                hideWhenEmpty
              />
              <DetailRow
                label="Actual"
                value={
                  action.actual_date
                    ? new Date(action.actual_date).toLocaleString()
                    : null
                }
                hideWhenEmpty
              />
              <DetailRow
                label="Description"
                value={action.description}
                hideWhenEmpty
              />
            </>
          ) : (
            <p className="tw:text-sm tw:text-muted-foreground">
              This {mention?.kind ?? 'item'} could not be found. It may have
              been archived or removed.
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
